import React from 'react'
import {
  Box,
  Flex,
  Text,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Avatar,
  Divider,
  Portal,
  useDisclosure,
} from '@chakra-ui/core'
import { useUserName } from 'hooks/useUserName'
import colors from 'constants/colors'
import { css } from '@emotion/core'
import useAvatar from 'hooks/useAvatar'
import { useLockScrollOnElementRole } from 'hooks/useLockScrollOnElementRole'

interface MessageReactionsProps {
  reactions: { [userId: string]: string }
}

export const MessageReactions: React.FC<MessageReactionsProps> = ({ reactions }) => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  useLockScrollOnElementRole('scrollableDiv', 'dialog')

  const emojiCount: { [emoji: string]: { count: number; users: string[] } } = {}
  let totalCount = 0

  if (reactions) {
    Object.entries(reactions).forEach(([userId, emoji]) => {
      if (!emojiCount[emoji]) {
        emojiCount[emoji] = { count: 0, users: [] }
      }
      emojiCount[emoji].count += 1
      emojiCount[emoji].users.push(userId)
      totalCount += 1
    })
  }

  if (!reactions || Object.keys(reactions).length === 0) {
    return null
  }

  return (
    <Box bg="#FFFFFF" borderRadius="10px" boxShadow="md" py="2px" px="5px" alignItems="center" position="relative">
      <Popover placement="auto" trigger="click" isOpen={isOpen} onClose={onClose}>
        <PopoverTrigger>
          <Box cursor="pointer" onClick={onOpen}>
            <Flex alignItems="center">
              {Object.entries(emojiCount)
                .slice(0, 5)
                .map(([emoji, { count }]) => (
                  <Flex key={emoji} alignItems="center" mx="2px">
                    <Text fontSize="14px">{emoji}</Text>
                  </Flex>
                ))}
              {totalCount > 1 && (
                <Text fontSize="12px" color={colors.greyDark} ml="2px">
                  {totalCount}
                </Text>
              )}
            </Flex>
          </Box>
        </PopoverTrigger>
        <PopoverContent
          width="auto"
          minW="300px"
          maxW="350px"
          border="none"
          borderRadius="10px"
          boxShadow="md"
          css={css`
            &:focus-visible {
              outline: none;
              box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
            }
          `}
        >
          <PopoverBody
            p="10px"
            borderRadius="10px"
            css={css`
              &:focus-visible {
                outline: none;
              }
            `}
          >
            <Box>
              <Box
                overflowX="auto"
                mb="8px"
                css={css`
                  &::-webkit-scrollbar {
                    height: 0px;
                    background: transparent;
                  }
                  -ms-overflow-style: none;
                  scrollbar-width: none;
                `}
              >
                <Flex minW="max-content" pb="5px">
                  <Flex alignItems="center" bg={colors.greyLight} borderRadius="20px" px="10px" py="5px" mr="5px">
                    <Text fontSize="12px" fontWeight="bold">
                      ALL {totalCount}
                    </Text>
                  </Flex>

                  {Object.entries(emojiCount).map(([emoji, { count }]) => (
                    <Flex
                      key={emoji}
                      alignItems="center"
                      borderRadius="20px"
                      px="10px"
                      py="5px"
                      mr="5px"
                      bg={colors.greyLight}
                    >
                      <Text fontSize="14px" mr="3px">
                        {emoji}
                      </Text>
                      <Text fontSize="12px" fontWeight="bold">
                        {count}
                      </Text>
                    </Flex>
                  ))}
                </Flex>
              </Box>

              <Divider my={0} />
              <Flex flexDir="column" style={{ gap: '5px' }} overflowY="auto" maxH="300px">
                {Object.entries(emojiCount).map(([emoji, { users }], index) => (
                  <Box key={emoji} mt="5px">
                    {users.map((userId, userIndex) => (
                      <React.Fragment key={userId}>
                        <ReactionUser userId={userId} emoji={emoji} />
                        {userIndex !== users.length - 1 && <Divider my="3px" />}
                      </React.Fragment>
                    ))}
                    {index !== Object.entries(emojiCount).length - 1 && <Divider my="5px" borderWidth="2px" />}
                  </Box>
                ))}
              </Flex>
            </Box>
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </Box>
  )
}

interface ReactionUserProps {
  userId: string
  emoji: string
}

const ReactionUser: React.FC<ReactionUserProps> = ({ userId, emoji }) => {
  const userName = useUserName(userId)
  const avatar = useAvatar(userId)

  return (
    <Flex alignItems="center" justifyContent="space-between" py="3px" minW="200px">
      <Flex alignItems="center">
        <Avatar src={avatar} size="xs" name={userName} />
        <Text fontSize="12px" ml="5px">
          {userName}
        </Text>
      </Flex>
      <Text fontSize="12px" mr="5px">
        {emoji}
      </Text>
    </Flex>
  )
}
